import { Component, signal } from "omi";
import isEmpty from "lodash-es/isEmpty";
import { v4 as uuid } from "uuid";

import { FilledEvent, fireEvent, ValidationEvents } from "@/components/validations/events";
import { DefaultApi, ValidationResponse, ValidationStatus } from "@/api";
import { Store } from "@/store";
import { decodeData, initApi } from "@/utils";

export abstract class ValidationBase<
  D extends ValidationResponse = ValidationResponse,
  P extends { optional?: boolean } = { optional?: boolean },
> extends Component<P> {
  private filled: boolean = false;
  private api: DefaultApi = initApi();
  protected optional: boolean = false;

  public status = signal<ValidationStatus>(ValidationStatus.Incomplete);

  public abstract isFilled(): boolean;
  public abstract processInternal(): Promise<ValidationStatus>;
  public abstract hydrate(validation: D | undefined): void;
  public abstract toDTO(): D;

  constructor() {
    super();
  }

  install() {
    this.filled = false;
    this.optional = this.props.optional || false;

    if (isEmpty(this.id)) {
      // This is just to support storybook, the app itself will attach the id to the elements themselves during content parsing.
      this.id = uuid();
    }

    this.hydrateBase();
  }

  updated() {
    const filled = this.isFilled();

    if (filled !== this.filled) {
      this.filled = filled;
      fireEvent(ValidationEvents.FILLED, { id: this.id, isFilled: filled } as FilledEvent);
    }
  }

  public isOptional(): boolean {
    return this.optional;
  }

  setStatusAndSave = async (status: ValidationStatus, updateSync: boolean = false) => {
    if (updateSync) {
      this.status.value = status;
      this.save(true, status);
    } else {
      await this.save(true, status);
      this.status.value = status;
    }
  };

  save = async (ignoreIsFilled: boolean = false, status?: ValidationStatus): Promise<boolean | undefined> => {
    if ((!ignoreIsFilled && !this.isFilled()) || Store.project.id.value == null) {
      return;
    }

    const dto = this.toDTO();
    if (status) {
      dto.status = status;
    }

    if (!Store.user.isLoggedIn.value) {
      // Store this temporarily in session storage
      sessionStorage.setItem(this.id, JSON.stringify(dto));
      return;
    }

    await this.api.submitValidation({
      validationId: dto.id,
      validationResponse: dto,
      projectId: Store.project.id.value,
    });
  };

  process = async (): Promise<ValidationStatus> => {
    const result = await this.processInternal();
    await this.setStatusAndSave(result);

    if (result === ValidationStatus.Complete) {
      fireEvent(ValidationEvents.COMPLETED, { id: this.id });
    } else {
      console.log("Validation not complete", this.id, result);
    }

    return result;
  };

  protected isEditable(): boolean {
    return Store.user.isPro() || this.status.value !== ValidationStatus.Complete;
  }

  private hydrateBase() {
    const isDataFromNonAuthFlow = sessionStorage.getItem(this.id);
    const data = this.getData();

    if (data == null) {
      return;
    }

    this.hydrate(data);
    this.updateSelf();

    /**
     * Once the user logs in and their info is prefilled,
     * we need to trigger a save for it to be persisted by the backend
     */
    if (isDataFromNonAuthFlow) {
      this.save();
    }

    fireEvent(ValidationEvents.HYDRATED, { id: this.id });
  }

  private getData(): D | undefined {
    let dataString = this.getAttribute("data");

    if (dataString) {
      // Clear session storage now that it is no longer required
      sessionStorage.removeItem(this.id);
      return decodeData<D>(dataString);
    }

    /**
     * Attempts to retrieve data saved in session storage.
     * Primary use case is the auth flow triggered by
     * a non-authenticated user submitting the validation.
     * @private
     */
    dataString = sessionStorage.getItem(this.id);

    if (dataString) {
      sessionStorage.removeItem(this.id);
      return JSON.parse(dataString);
    }

    return undefined;
  }
}
